<template>
  <aside class="admin-sidebar">
    <div class="brand">
      <div class="brand-icon">M</div>
      <div>
        <div class="brand-title">管理后台</div>
        <div class="brand-subtitle">System Console</div>
      </div>
    </div>

    <div class="menu-groups">
      <div v-for="group in menuGroups" :key="group.name" class="menu-group">
        <div class="group-title">{{ group.name }}</div>
        <div
          v-for="item in group.items"
          :key="item.path"
          :class="['menu-item', { active: isActive(item), disabled: item.disabled }]"
          @click="handleClick(item)"
        >
          <span>{{ item.title }}</span>
          <span v-if="item.path === '/admin/feedback' && feedbackUnreadCount > 0" class="unread-badge">{{ feedbackUnreadCount > 99 ? '99+' : feedbackUnreadCount }}</span>
          <span v-if="item.disabled" class="coming-soon">开发中</span>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import { fetchAdminFeedbackUnreadCount } from "@/services/feedback";
import { authState } from "@/services/auth";
import { isPlatformAdmin, isRagAdmin } from "@/services/adminAccess";

export default {
  name: "AdminSidebar",
  data() {
    return {
      feedbackUnreadCount: 0,
      allMenuGroups: [
        {
          name: "运营报告",
          items: [
            { title: "综合概览", path: "/admin/operation-reports" },
            { title: "用户增长", path: "/admin/operation-reports/users" },
            { title: "用户使用统计", path: "/admin/operation-reports/user-usage" },
            { title: "付费转化", path: "/admin/operation-reports/payments" },
            { title: "模型成本", path: "/admin/operation-reports/models" },
            { title: "邀请拉新", path: "/admin/operation-reports/invites" },
            { title: "兑换码效果", path: "/admin/operation-reports/redemptions" },
          ],
        },
        {
          name: "运营中心",
          items: [
            { title: "任务列表", path: "/admin/tasks" },
            { title: "案例维护", path: "/admin/app-cases" },
            { title: "工具配置", path: "/admin/tool-apps" },
            { title: "社区管理", path: "/admin/community" },
            { title: "用户管理", path: "/admin/users" },
            { title: "邀请统计", path: "/admin/invites" },
            { title: "兑换码管理", path: "/admin/redemption" },
            { title: "用户反馈", path: "/admin/feedback" },
            { title: "公告管理", path: "/admin/announcements" },
            { title: "支付订单", path: "/admin/payment-orders" },
          ],
        },
        {
          name: "应用管理",
          items: [
            { title: "应用设置", path: "/admin/applications" },
          ],
        },
        {
          name: "权限管理",
          items: [
            { title: "管理员管理", path: "/admin/administrators" },
          ],
        },
        {
          name: "RAG知识库",
          items: [
            { title: "素材库", path: "/admin/rag/materials" },
            { title: "深度分析", path: "/admin/rag/deep-analysis" },
            { title: "RAG任务", path: "/admin/rag/tasks" },
            { title: "检索测试", path: "/admin/rag/search" },
          ],
        },
        {
          name: "会员积分",
          items: [
            { title: "用户积分", path: "/admin/credits" },
            { title: "积分变动", path: "/admin/credit-ledger" },
            { title: "积分加油包", path: "/admin/credit-packages" },
            { title: "存储扩容包", path: "/admin/storage-packages" },
            { title: "会员设置", path: "/admin/membership-plans" },
            { title: "积分活动", path: "/admin/system-config" },
            { title: "会员管理", path: "/admin/memberships", disabled: true },
          ],
        },
        {
          name: "模型与计费",
          items: [
            { title: "模型线路", path: "/admin/model-catalog" },
            { title: "容量诊断", path: "/admin/pipeline-capacity" },
            { title: "回调并发", path: "/admin/callback-lanes" },
            { title: "模型大类", path: "/admin/model-families" },
            { title: "模型用量", path: "/admin/model-usage" },
          ],
        },
      ],
    };
  },
  computed: {
    currentUser() {
      return authState.session && authState.session.user;
    },
    isPlatformAdmin() {
      return isPlatformAdmin(this.currentUser);
    },
    menuGroups() {
      if (this.isPlatformAdmin) {
        return this.allMenuGroups;
      }
      if (isRagAdmin(this.currentUser)) {
        return this.allMenuGroups.filter((group) => group.name === "RAG知识库");
      }
      return [];
    },
  },
  methods: {
    isActive(item) {
      return this.$route.path === item.path;
    },
    handleClick(item) {
      if (item.disabled) {
        this.$message.info("这个后台模块还在开发中");
        return;
      }
      if (this.$route.path !== item.path) {
        this.$router.push(item.path);
      }
    },
    async loadFeedbackUnreadCount() {
      if (!this.isPlatformAdmin) {
        this.feedbackUnreadCount = 0;
        return;
      }
      try {
        this.feedbackUnreadCount = await fetchAdminFeedbackUnreadCount();
      } catch (_) { /* ignore */ }
    },
  },
  mounted() {
    this.loadFeedbackUnreadCount();
  },
  watch: {
    '$route.path': function () {
      this.loadFeedbackUnreadCount();
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-sidebar {
  width: 220px;
  min-height: 100vh;
  background: #1f2430;
  color: #d9deea;
  border-right: 1px solid rgba(255, 255, 255, 0.06);
}

.brand {
  height: 72px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.brand-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #fe752b 0%, #ff9805 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
}

.brand-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.brand-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #98a2b3;
}

.menu-groups {
  padding: 18px 12px;
}

.menu-group + .menu-group {
  margin-top: 8px;
}

.group-title {
  position: relative;
  padding: 12px 12px 8px 18px;
  font-size: 15px;
  font-weight: 700;
  color: #f2f5f9;
  letter-spacing: 0.03em;
}

.group-title::before {
  content: "";
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 16px;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffb05c 0%, #fe752b 100%);
}

.menu-item {
  height: 42px;
  margin-left: 18px;
  padding: 0 12px 0 18px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  color: #b6c0cf;
  font-size: 14px;
  font-weight: 500;
  position: relative;
}

.menu-item::before {
  content: "";
  position: absolute;
  left: 6px;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  transform: translateY(-50%);
  background: rgba(162, 176, 197, 0.7);
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e8edf5;
}

.menu-item.active {
  background: rgba(247, 128, 1, 0.18);
  color: #ffd2a1;
  font-weight: 600;
}

.menu-item.active::before {
  background: #ff9a3d;
}

.menu-item.disabled {
  color: #7b8494;
}

.menu-item.disabled::before {
  background: rgba(123, 132, 148, 0.55);
}

.coming-soon {
  font-size: 12px;
  color: #7b8494;
}

.unread-badge {
  display: inline-block;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #f56c6c;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
}
</style>
